import React, { useState } from 'react';
import { Button, Form, Input, Modal, Space, Typography, Popconfirm, message } from 'antd';
import { changePasswordAuthenticated } from '../api/passwordClient';
import { resignTeamMember } from '../api/teamClient';

export default function SettingsPanel({ token, isTeamMember, onLogout }) {
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [resignLoading, setResignLoading] = useState(false);
  const [form] = Form.useForm();

  const submitPassword = async (values) => {
    setPwdLoading(true);
    try {
      await changePasswordAuthenticated(
        token,
        values.currentPassword,
        values.newPassword
      );
      message.success('Password updated. Please sign in again.');
      setPwdOpen(false);
      form.resetFields();
      onLogout?.();
    } catch (e) {
      message.error(
        e.response?.data?.message || e.message || 'Could not update password'
      );
    } finally {
      setPwdLoading(false);
    }
  };

  const doResign = async () => {
    setResignLoading(true);
    try {
      await resignTeamMember(token);
      message.success('You have resigned.');
      onLogout?.();
    } catch (e) {
      message.error(e.response?.data?.message || e.message || 'Could not resign');
    } finally {
      setResignLoading(false);
    }
  };

  return (
    <>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Typography.Text type="secondary">
          Manage your account security and team membership.
        </Typography.Text>

        <Button type="primary" block onClick={() => setPwdOpen(true)}>
          Change password
        </Button>

        {isTeamMember ? (
          <Popconfirm
            title="Resign from team?"
            description="Your login will be removed from this organization. You will be signed out."
            onConfirm={doResign}
            okText="Yes, resign"
            cancelText="Cancel"
            okButtonProps={{ loading: resignLoading }}
          >
            <Button danger block loading={resignLoading}>
              Resign
            </Button>
          </Popconfirm>
        ) : null}
      </Space>

      <Modal
        title="Change password"
        open={pwdOpen}
        onCancel={() => {
          setPwdOpen(false);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
        width={400}
      >
        <Form form={form} layout="vertical" onFinish={submitPassword}>
          <Form.Item
            name="currentPassword"
            label="Current password"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New password"
            rules={[{ required: true, message: 'Required' }, { min: 5, message: 'At least 5 characters' }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm new password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Required' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('New passwords do not match!')
                  );
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={pwdLoading} block>
              Update password
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
